// Game state, levels and worlds, view (scaling) and screen switching
import { Registry } from '../core/registry';
import { Physics } from '../core/physics';
import { SFX } from '../core/audio';
import { WORLDS as WORLD_DEFS } from '../worlds';
import { cv, $ } from './dom';
import { Store, type Profile } from '../core/store';
import { worldName } from '../core/i18n';
import type { Level, Placement, SimState, Vec } from '../core/types';
import { hideHand } from './hud';
import { updatePartMenu } from './input';

export const W = Physics.W, H = Physics.H, DEG = Math.PI / 180;

// Icon and texts come from the world's meta (src/worlds/<key>/world.ts and strings.ts); n is the name in the current language
export interface WorldInfo { ic: string; readonly n: string; k: string }
export const WORLDS: WorldInfo[] = WORLD_DEFS.map(d => ({ ic: d.meta!.icon, get n() { return worldName(d.meta!.strings); }, k: d.k }));

/** A level as the game uses it: the fields every level has, plus w = index in WORLDS (set above) */
export type GameLevel = Level & { id: string; wk: string; wi: number; w: number; tools: Record<string, number>; solution: Placement[]; learn: string[] };

// All levels in one long row, world by world. G.li is the index in that row; progress is saved per levelKey
export const LEVELS: GameLevel[] = [];
export const WSTART: number[] = [];
WORLDS.forEach((w, wi) => {
  const def = Registry.worlds[w.k];
  WSTART[wi] = LEVELS.length;
  if (!def) return;
  if (def.sounds) for (const [n, f] of Object.entries(def.sounds)) SFX.define(n, f);
  if (def.meta) SFX.defineMusic(def.k, def.meta.music);
  def.levels!.forEach(L => { L.w = wi; LEVELS.push(L as GameLevel); });
});
const worldHas = (wi: number) => WSTART[wi] != null && (WSTART[wi + 1] != null ? WSTART[wi + 1] : LEVELS.length) > WSTART[wi];
export function worldOpen(wi: number) {
  if (!worldHas(wi)) return false;
  if (Store.d.settings.testAll || wi === 0) return true;
  return !!prog(WSTART[wi] - 1);
}

// ---------- Canvas and view ----------
export let DPR = 1, V = { s: 1, ox: 0, oy: 0 };
export function resize() {
  DPR = Math.min(2, window.devicePixelRatio || 1);
  cv.width = Math.round(innerWidth * DPR); cv.height = Math.round(innerHeight * DPR);
  const cs = getComputedStyle(document.documentElement);
  const top = parseFloat(cs.getPropertyValue('--top')) || 68, tool = parseFloat(cs.getPropertyValue('--tool')) || 132;
  const aw = innerWidth - tool - 8, ah = innerHeight - top - 8;
  const s = Math.min(aw / W, ah / H);
  V = { s, ox: 6 + (aw - W * s) / 2, oy: top + (ah - H * s) / 2 };
  updatePartMenu();
}
addEventListener('resize', resize);
export const toWorld = (cx: number, cy: number): Vec => ({ x: (cx - V.ox) / V.s, y: (cy - V.oy) / V.s });
export const toScreen = (x: number, y: number): Vec => ({ x: V.ox + x * V.s, y: V.oy + y * V.s });

// ---------- Game state ----------
/** Effect particle (dots, confetti, notes, hearts) */
export interface Particle { x: number; y: number; vx: number; vy: number; life: number; max: number; col: string; r: number; kind: string; rot: number; [extra: string]: unknown }
/** A part being dragged: index in G.pl, grab offset, start of the press, and where it came from */
export interface Drag { idx: number; off: Vec; isNew: boolean; sx: number; sy: number; moved: boolean; from: Vec | null; overBox?: boolean }
export interface GameState {
  li: number;
  /** Set by loadLevel before the game screen is shown */
  level: GameLevel;
  pl: Placement[];
  /** The simulation (Physics.create); set by loadLevel/rebuild */
  S: SimState;
  state: 'idle' | 'build' | 'play' | 'won' | 'failing';
  sel: number;
  drag: Drag | null;
  hintStep: number;
  hintT?: number;
  fails: number;
  t: number;
  fx: Particle[];
  shake: number;
  lastWiii: number;
  doneT: number;
  acc: number;
  lastTS: number;
  token?: number;
  everPlayed?: boolean;
  playT?: number;
  /** World shown on the map, and the world picked in the world overlay */
  mapW?: number | null;
  pickW?: number | null;
  /** Test shots (#test=...:freeze): G.t stands still */
  freeze?: boolean;
  bgv?: number;
  /** Background and static bodies at screen resolution */
  cache?: { L: GameLevel; k: number; c: HTMLCanvasElement; v: number };
}
export const G: GameState = { li: 0, level: null as unknown as GameLevel, pl: [], S: null as unknown as SimState, state: 'idle', sel: -1, drag: null, hintStep: 0, fails: 0, t: 0, fx: [], shake: 0, lastWiii: -999, doneT: 0, acc: 0, lastTS: 0 };

// Key in saved data (prog, fails): '<world>/<level id>', e.g. 'dishes/first-roll'
export const levelKey = (li: number) => LEVELS[li] ? LEVELS[li].wk + '/' + LEVELS[li].id : '';
export const progOf = (p: Profile | null | undefined, li: number) => (p && p.prog && p.prog[levelKey(li)]) || null;
function prog(li: number) { return progOf(Store.prof(), li); }
export function payLocked(li: number) { return li >= 3 && !Store.d.unlocked && !Store.d.settings.testAll; }

export function usedOf(t: string) { return G.pl.filter(p => p.t === t).length; }

// ---------- Screens ----------
export type ScreenId = 'scrTitle' | 'scrProfiles' | 'scrMap' | 'game';
export function show(id: ScreenId) {
  for (const s of ['scrTitle', 'scrProfiles', 'scrMap']) $('#' + s).hidden = s !== id;
  $('#game').hidden = id !== 'game';
  $('#corner').hidden = !(id === 'scrProfiles' || id === 'scrMap');
  if (id !== 'game') { G.state = 'idle'; hideHand(); updatePartMenu(); }
  if (id === 'game') { resize(); }
  if (id !== 'game' && id !== 'scrTitle') SFX.track('menu');
}
